import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useModuleRewards } from "@/hooks/useModuleRewards";
import { useCurriculumProgress } from "@/hooks/useCurriculumProgress";
import ModuleShell from "@/components/modules/ModuleShell";
import ModuleStepCard from "@/components/modules/ModuleStepCard";
import ModuleQuiz from "@/components/modules/ModuleQuiz";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ArrowRight, Users, Heart, Target, ShieldCheck, CheckCircle2, Baby, Scale, MessageCircle } from "lucide-react";

type Step = "intro" | "transparency" | "shared_goals" | "household_split" | "dependents" | "risk_talk" | "legacy" | "quiz" | "summary";
const STEPS: Step[] = ["intro", "transparency", "shared_goals", "household_split", "dependents", "risk_talk", "legacy", "quiz", "summary"];

const FamilyBudgetModule = () => {
  const navigate = useNavigate();
  const { moduleGates, loading } = useCurriculumProgress();
  const { completeModule } = useModuleRewards();
  const [step, setStep] = useState<Step>("intro");
  const [goalChoice, setGoalChoice] = useState<string | null>(null);
  const [splitStyle, setSplitStyle] = useState<string | null>(null);
  const [childPlanPct, setChildPlanPct] = useState(15);
  const [completed, setCompleted] = useState(false);

  const gate = moduleGates.find(g => g.moduleKey === "family_budget");
  const isLocked = !gate?.isUnlocked;
  if (loading) return null;

  return (
    <ModuleShell
      title="Family Budget & Planning"
      stepIndex={STEPS.indexOf(step)}
      totalSteps={STEPS.length}
      isLocked={isLocked}
      lockMessage={gate?.missingPrereqs?.length ? `Complete: ${gate.missingPrereqs.join(", ")}` : "Complete Family Finance to unlock."}
      isCompleted={completed}
      completedMessage="You've built a family financial framework with shared goals and planning."
      stepKey={step}
    >
      {step === "intro" && (
        <ModuleStepCard
          icon={<Users className="w-8 h-8 text-primary" />}
          title="Family Finance Planning"
          body="Money in a household isn't just about numbers — it's about communication, shared goals, and planning for people who depend on you."
          highlights={["Financial transparency builds trust", "Shared goals prevent conflict", "Planning for dependents is non-negotiable"]}
          action={<Button onClick={() => setStep("transparency")} className="w-full gap-2">Let's Begin <ArrowRight className="w-4 h-4" /></Button>}
        />
      )}

      {step === "transparency" && (
        <ModuleStepCard
          icon={<MessageCircle className="w-8 h-8 text-accent" />}
          title="Financial Transparency"
          body="Money is the #1 source of relationship conflict. Transparency doesn't mean merging everything — it means open communication about income, debt, spending habits, and financial goals."
          highlights={["Discuss debts openly before they become surprises", "Regular money check-ins prevent drift", "Transparency ≠ control — it means alignment"]}
          action={<Button onClick={() => setStep("shared_goals")} className="w-full gap-2">Next: Shared Goals <ArrowRight className="w-4 h-4" /></Button>}
        />
      )}

      {step === "shared_goals" && (
        <div className="space-y-6">
          <ModuleStepCard icon={<Target className="w-8 h-8 text-primary" />} title="Set a Shared Goal" body="Families need at least one financial goal they're working toward together:" action={<div />} />
          <div className="grid gap-3">
            {[
              { key: "home", label: "Buy a Home Together", desc: "Save for a down payment, build equity, create stability." },
              { key: "education", label: "Fund Education", desc: "529 plans, college savings, or skill-building for the family." },
              { key: "emergency", label: "6-Month Emergency Fund", desc: "Full family protection against job loss or medical events." },
              { key: "retire", label: "Early Retirement", desc: "Aggressive saving and investing to retire before 60." },
            ].map(g => (
              <button key={g.key} className={`rounded-2xl border p-4 text-left transition-all ${goalChoice === g.key ? "border-primary bg-primary/10" : "border-border bg-card/50 hover:border-primary/30"}`} onClick={() => setGoalChoice(g.key)}>
                <p className="font-heading font-bold text-sm text-foreground">{g.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{g.desc}</p>
              </button>
            ))}
          </div>
          <Button onClick={() => setStep("household_split")} disabled={!goalChoice} className="w-full gap-2">Continue <ArrowRight className="w-4 h-4" /></Button>
        </div>
      )}

      {step === "household_split" && (
        <div className="space-y-6">
          <ModuleStepCard icon={<Scale className="w-8 h-8 text-accent" />} title="Household Expense Split" body="How should shared expenses be divided? There's no single right answer." action={<div />} />
          <div className="grid gap-3">
            {[
              { key: "5050", label: "50/50 Split", desc: "Equal split regardless of income. Simple but can be unfair if incomes differ." },
              { key: "proportional", label: "Proportional to Income", desc: "Each person contributes based on what they earn. Fairer for unequal incomes." },
              { key: "pooled", label: "Fully Pooled", desc: "All income goes into one pot. Maximum partnership, requires maximum trust." },
            ].map(s => (
              <button key={s.key} className={`rounded-2xl border p-4 text-left transition-all ${splitStyle === s.key ? "border-primary bg-primary/10" : "border-border bg-card/50 hover:border-primary/30"}`} onClick={() => setSplitStyle(s.key)}>
                <p className="font-heading font-bold text-sm text-foreground">{s.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.desc}</p>
              </button>
            ))}
          </div>
          <Button onClick={() => setStep("dependents")} disabled={!splitStyle} className="w-full gap-2">Next: Dependents <ArrowRight className="w-4 h-4" /></Button>
        </div>
      )}

      {step === "dependents" && (
        <div className="space-y-6">
          <ModuleStepCard
            icon={<Baby className="w-8 h-8 text-primary" />}
            title="Planning for Dependents"
            body="Children cost an average of $15,000-$17,000 per year. How much of your household budget should be allocated to child-related expenses?"
            subtext={`Child allocation: ${childPlanPct}% of household budget`}
            action={<div />}
          />
          <Slider value={[childPlanPct]} min={5} max={35} step={1} onValueChange={([v]) => setChildPlanPct(v)} />
          <div className="flex justify-between text-xs text-muted-foreground"><span>5%</span><span>35%</span></div>
          <div className="rounded-2xl border border-border bg-card/50 p-4">
            <p className="text-xs text-muted-foreground">Includes childcare, food, clothing, activities, education savings, and healthcare co-pays.</p>
          </div>
          <Button onClick={() => setStep("risk_talk")} className="w-full gap-2">Next: Risk Conversations <ArrowRight className="w-4 h-4" /></Button>
        </div>
      )}

      {step === "risk_talk" && (
        <ModuleStepCard
          icon={<ShieldCheck className="w-8 h-8 text-accent" />}
          title="Family Risk Conversations"
          body="Families must discuss: What happens if someone loses their job? What if there's a medical emergency? What if one person can't work? These aren't pessimistic questions — they're protective ones."
          highlights={["Life insurance protects dependents", "Disability insurance protects income", "Estate planning protects legacy", "Having a plan reduces panic"]}
          action={<Button onClick={() => setStep("legacy")} className="w-full gap-2">Next: Legacy Planning <ArrowRight className="w-4 h-4" /></Button>}
        />
      )}

      {step === "legacy" && (
        <ModuleStepCard
          icon={<Heart className="w-8 h-8 text-primary" />}
          title="Intergenerational Planning"
          body="Legacy isn't just about leaving money behind. It's about building financial literacy, naming beneficiaries, creating wills, and teaching the next generation how money works."
          highlights={["Name beneficiaries on all accounts", "Create a basic will and power of attorney", "Teach children about money early", "Build systems, not just savings"]}
          action={<Button onClick={() => setStep("quiz")} className="w-full gap-2">Take Knowledge Check <ArrowRight className="w-4 h-4" /></Button>}
        />
      )}

      {step === "quiz" && (
        <ModuleQuiz moduleKey="family_budget" onPass={() => { setCompleted(true); setStep("summary"); completeModule("family_budget"); }} />
      )}

      {step === "summary" && (
        <div className="space-y-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto border border-primary/20">
            <CheckCircle2 className="w-8 h-8 text-primary" />
          </div>
          <h2 className="font-heading text-xl font-bold text-foreground">Family Planning Complete</h2>
          <p className="text-sm text-muted-foreground">You've built a family financial framework. Shared goals, transparency, and planning protect everyone.</p>
          <Button onClick={() => navigate("/dashboard")} className="w-full">Back to Dashboard</Button>
        </div>
      )}
    </ModuleShell>
  );
};

export default FamilyBudgetModule;
